const DB_NAME = "baysport-journal";
const DB_VERSION = 1;

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("sessions")) {
        db.createObjectStore("sessions");
      }
      if (!db.objectStoreNames.contains("journals")) {
        db.createObjectStore("journals");
      }
      if (!db.objectStoreNames.contains("mutations")) {
        const store = db.createObjectStore("mutations", {
          keyPath: "id",
          autoIncrement: true,
        });
        store.createIndex("tenantKey", "tenantKey", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function txStore(db, storeName, mode) {
  return db.transaction(storeName, mode).objectStore(storeName);
}

async function idbGet(storeName, key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = txStore(db, storeName, "readonly").get(key);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(storeName, key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = txStore(db, storeName, "readwrite").put(value, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function idbDelete(storeName, key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = txStore(db, storeName, "readwrite").delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export function sessionsCacheKey(tenantKey, date, groupId = "") {
  return `${tenantKey}:sessions:${date}:${groupId || ""}`;
}

export function journalCacheKey(tenantKey, sessionId) {
  return `${tenantKey}:journal:${sessionId}`;
}

export async function cacheSessions(tenantKey, date, groupId, sessions) {
  const key = sessionsCacheKey(tenantKey, date, groupId);
  await idbPut("sessions", key, {
    sessions,
    cachedAt: Date.now(),
  });
}

export async function readCachedSessions(tenantKey, date, groupId) {
  const key = sessionsCacheKey(tenantKey, date, groupId);
  const row = await idbGet("sessions", key);
  return row?.sessions ?? null;
}

export async function cacheJournal(tenantKey, sessionId, session) {
  const key = journalCacheKey(tenantKey, sessionId);
  await idbPut("journals", key, {
    session,
    cachedAt: Date.now(),
  });
}

export async function readCachedJournal(tenantKey, sessionId) {
  const key = journalCacheKey(tenantKey, sessionId);
  const row = await idbGet("journals", key);
  return row?.session ?? null;
}

export async function enqueueMutation(tenantKey, mutation) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = txStore(db, "mutations", "readwrite").add({
      tenantKey,
      sessionId: mutation.sessionId,
      patientId: mutation.patientId,
      payload: mutation.payload,
      createdAt: Date.now(),
      retries: 0,
    });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function listPendingMutations(tenantKey) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const store = txStore(db, "mutations", "readonly");
    const idx = store.index("tenantKey");
    const req = idx.getAll(tenantKey);
    req.onsuccess = () => {
      const rows = (req.result || []).sort((a, b) => a.createdAt - b.createdAt);
      resolve(rows);
    };
    req.onerror = () => reject(req.error);
  });
}

export async function removeMutation(id) {
  await idbDelete("mutations", id);
}

export async function countPendingMutations(tenantKey) {
  const rows = await listPendingMutations(tenantKey);
  return rows.length;
}

export function applyLocalAttendanceMark(journal, patientId, status, absence_reason, payload) {
  if (!journal) return journal;
  const attendances = (journal.attendances || []).map((a) => {
    if (a.patient_id !== patientId) return a;
    if (payload?.clear) {
      return {
        ...a,
        status: null,
        marked_at: null,
        absence_reason: null,
      };
    }
    return {
      ...a,
      status: payload?.status || status,
      marked_at: new Date().toISOString(),
      absence_reason: absence_reason || null,
    };
  });
  return { ...journal, attendances };
}
